import { useEffect, useState } from "react";
import Header from "../../components/Header";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { applyResignationSchema } from "../../schemas/applyResignationSchema";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { 
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import {
  getApplyResignationData,
  submitResignationData,
} from "../../services/exitManagement";
import { Link, useNavigate } from "react-router-dom";
import useLoader from "../../hooks/useLoader";
import { defaultDateFormatter } from "../../Helper/functionHelper";
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ApplyResignation = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const [isBuyoutAllowed, setIsBuyoutAllowed] = useState(false);
  const [desiredLastWorkingDay, setDesiredLastWorkingDay] = useState(null);
  const [actualLastWorkingDay, setActualLastWorkingDay] = useState(null);
  const [noticePeriodDays, setNoticePeriodDays] = useState(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(applyResignationSchema),
    defaultValues: {
      email: "",
      reason: "",
      otherReason: "",
      noticePeriodDays: 0,
      buyout: false,
      buyoutDays: 0,
      desiredLastWorkingDay: null,
      actualLastWorkingDay: null,
    },
  });

  const reason = watch("reason");
  const isBuyoutChecked = watch("buyout");

  const handleFileUpload = (e) => {
    if (e.target.files) {
      const allowedFileTypes = [
        ".doc",
        ".docx",
        ".pdf",
        ".xls",
        ".xlsx",
        ".jpg",
        ".jpeg",
        ".png",
      ];
      const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes

      const filesArray = Array.from(e.target.files);
      const invalidTypeFiles = [];
      const oversizedFiles = [];
      const validFiles = [];

      filesArray.forEach((file) => {
        const extension = "." + file.name.split(".").pop().toLowerCase();
        const isValidType = allowedFileTypes.includes(extension);
        const isValidSize = file.size <= maxFileSize;

        if (!isValidType) {
          invalidTypeFiles.push(file);
        } else if (!isValidSize) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);

      if (invalidTypeFiles.length > 0 || oversizedFiles.length > 0) {
        let message = "";

        if (invalidTypeFiles.length > 0) {
          message += `${invalidTypeFiles.length} file(s) were rejected due to invalid file type. Only doc, docx, pdf, xls, xlsx, jpg, jpeg, png are allowed.\n\n`;
        }

        if (oversizedFiles.length > 0) {
          message += `${oversizedFiles.length} file(s) were rejected due to file size exceeding 2MB limit.`;
        }

        toast.error(message);
      }
      e.target.value = null;
    }
  };

  const removeDocument = (index) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, fileIndex) => fileIndex !== index)
    );
  };

  const submitResignation = async (data) => {
    try {
      const formData = new FormData();

      // Append non-file fields
      formData.append("email", data.email);
      formData.append("reason", data.reason || "");
      formData.append("otherReason", data.otherReason || "");
      formData.append("noticePeriodDays", data.noticePeriodDays || 0);
      formData.append(
        "desiredLastWorkingDay",
        data.desiredLastWorkingDay?.toISOString() || ""
      );
      formData.append(
        "actualLastWorkingDay",
        data.actualLastWorkingDay?.toISOString() || ""
      );
      formData.append("buyout", data.buyout ? "true" : "false");
      formData.append("buyoutDays", data.buyoutDays || 0);

      // Append files
      uploadedFiles.forEach((file, index) => {
        formData.append("documents", file); // Key must match your backend field name
      });

      // Submit the form data using axios
      const response = await submitResignationData(formData);

      if (response.status === 200 || response.status === 201) {
        toast.success(response.data.message);
        navigate("/exit-management");
      }
    } catch (error) {
      console.error("Error submitting resignation:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to submit resignation application"
      );
    }
  };

  const fetchResignationData = async () => {
    try {
      showLoader();
      const resignationData = await getApplyResignationData();
      if (resignationData.status === 200) {
        const { data } = resignationData;
        const { resignationEmail, noticePeriodDays, desiredLastWorkingDay } =
          data;
        const desiredLastWorkingDayDate = new Date(desiredLastWorkingDay);
        setDesiredLastWorkingDay(desiredLastWorkingDayDate);
        reset({
          email: resignationEmail,
          noticePeriodDays: noticePeriodDays,
          desiredLastWorkingDay: desiredLastWorkingDayDate,
          actualLastWorkingDay: desiredLastWorkingDayDate,
        });
        setActualLastWorkingDay(desiredLastWorkingDayDate);
        setNoticePeriodDays(noticePeriodDays);
        setIsBuyoutAllowed(data.isBuyoutAllowed);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      hideLoader();
    }
  };
  // fetch resignation data
  useEffect(() => {
    fetchResignationData();
  }, []);
  // console.log(actualLastWorkingDay);

  return (
    <>
      <Header title="Apply Resignation">
        <span className="add float-end">
          <Link to="/exit-management" className="btn btn-secondary btn-sm">
            <span class="icon">
              <img src="/public/images/back-arrow.svg" alt="back" />
            </span>
            <span className="text">Back</span>
          </Link>
        </span>
      </Header>
      <form onSubmit={handleSubmit(submitResignation)} className="p-3">
        <div className="row">
          {/* email field */}
          <div className="col-md-12">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Email for Resignation"
                  variant="standard"
                  multiline
                  rows={10}
                  {...field}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                  disabled={isSubmitting}
                />
              )}
            />
          </div>
        </div>

        <div className="row mt-3 row-gap-2">
          {/* reason for resignation field */}
          <div className="col-md-4">
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="standard"
                  error={!!errors.reason}
                  helperText={errors.reason?.message}
                  disabled={isSubmitting}
                >
                  <InputLabel>Reason for Resignation*</InputLabel>
                  <Select
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    value={field.value}
                    disabled={isSubmitting}
                  >
                    <MenuItem value="">Select reason</MenuItem>
                    <MenuItem value="relocation">Relocation</MenuItem>
                    <MenuItem value="personal">Personal Reason</MenuItem>
                    <MenuItem value="new_opportunity">New Opportunity</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  <FormHelperText>{errors.reason?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </div>

          {/* remarks field */}
          {reason === "other" && (
            <div className="col-md-4 mb-3">
              <TextField
                label="Please specify the reason"
                variant="standard"
                fullWidth
                {...register("otherReason")}
                error={!!errors.otherReason}
                helperText={errors.otherReason?.message}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* notice period field */}
          {/* <div className="col-md-4">
            <TextField
              label="Notice Period Days"
              type="number"
              variant="standard"
              fullWidth
              {...register("noticePeriodDays", { valueAsNumber: true })}
              error={!!errors.noticePeriodDays}
              helperText={errors.noticePeriodDays?.message}
              disabled={true}
            />
          </div> */}
          <div className="col-md-4">
            <span>Notice Period Days</span>
            <p>{noticePeriodDays}</p>
          </div>

          {/* actual last working day field */}
          {/* <div className="col-md-4">
            <Controller
              name="actualLastWorkingDay"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Actual Last Working Day"
                  {...field}
                  onChange={(date) => field.onChange(date)}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  minDate={new Date()}
                  className="pb-0"
                  slotProps={{
                    textField: {
                      variant: "standard",
                      fullWidth: true,
                      error: !!errors.actualLastWorkingDay,
                      helperText: errors.actualLastWorkingDay?.message,
                    },
                  }}
                  disabled={true}
                />
              )}
            />
          </div> */}
          <div className="col-md-4">
            <span>Actual Last Working Day</span>
            <p>{defaultDateFormatter(actualLastWorkingDay)}</p>
          </div>

          {/* desired last working day field */}
          <div className="col-md-4">
            <Controller
              name="desiredLastWorkingDay"
              control={control}
              render={({ field }) => (
                <>
                  <DatePicker
                    label="Desired Last Working Day"
                    {...field}
                    onChange={(date) => field.onChange(date)}
                    value={field.value || null}
                    format="dd MMM yyyy"
                    minDate={new Date()}
                    maxDate={new Date(desiredLastWorkingDay)}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        fullWidth: true,
                        error: !!errors.desiredLastWorkingDay,
                        helperText: errors.desiredLastWorkingDay?.message,
                      },
                    }}
                    disabled={isSubmitting}
                  />
                </>
              )}
            />
          </div>

          {/*buyout field*/}
          {isBuyoutAllowed && (
            <div className="col-md-4">
              <FormControlLabel
                control={
                  <Controller
                    name="buyout"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        disabled={isSubmitting}
                        color="primary"
                      />
                    )}
                  />
                }
                label="Request for Buyout"
              />
            </div>
          )}

          {/* buyout days field */}
          {isBuyoutAllowed && isBuyoutChecked && (
            <div className="col-md-4">
              <TextField
                label="Buyout Period Days"
                type="number"
                variant="standard"
                fullWidth
                {...register("buyoutDays", { valueAsNumber: true })}
                error={!!errors.buyoutDays}
                helperText={errors.buyoutDays?.message}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* documents field */}
          <div className="col-md-4">
            <InputLabel id="documents-label">Documents</InputLabel>
            <Button
              component="label"
              variant="contained"
              color="success"
              size="small"
              startIcon={<CloudUploadIcon />}
              disabled={isSubmitting}
            >
              Upload files
              <VisuallyHiddenInput
                type="file"
                multiple
                onChange={handleFileUpload}
                accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
              />
            </Button>
            <div className="mt-2">
              {uploadedFiles.map((file, idx) => (
                <div key={idx}>
                  {file.name}
                  <span
                    size="small"
                    onClick={() => {
                      removeDocument(idx);
                    }}
                    className="text-danger cursor-pointer"
                  >
                    <CloseIcon />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default ApplyResignation;
